import axios from "axios";
import { UserProfile } from "../../api/AuthAPI";
import axiosInstance from "../../api/axiosInstance";
import { GetUserCoupon } from "../../api/VoucherAPI";
import { SHAKE_HQ_APP, SHAKE_HQ_API_KEY, SHAKE_HQ_CUSTOMER_SEND_PATH } from "../../configs/config";
import { PATH_NAME } from "../../configs/pathName";
import { normalizeMachineCouponList, normalizeMachineRewardPoints } from "./machineLoginData";

export const decodeMachineLoginPayload = (qValue: string | null) => {
  if (!qValue) {
    return {
      machine_id: null,
      machine_session: null,
    };
  }

  try {
    const decoded = window.atob(qValue);
    const [machineIdPart, machineSessionPart] = decoded.split('#');

    return {
      machine_id: Number(machineIdPart) || null,
      machine_session: machineSessionPart || null,
    };
  } catch {
    return {
      machine_id: null,
      machine_session: null,
    };
  }
};

export const sendMachineLoginToShakeHQ = async (payload: Record<string, any>) => {
  if (!SHAKE_HQ_API_KEY) {
    throw new Error("Missing REACT_APP_SHAKE_HQ_API_KEY environment variable.");
  }

  try {
    const response = await axios.post(`${SHAKE_HQ_APP}${SHAKE_HQ_CUSTOMER_SEND_PATH}`, payload, {
      headers: {
        Accept: "/",
        "api-key": SHAKE_HQ_API_KEY,
        "Content-Type": "application/json",
      },
      timeout: 20000,
    });

    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }

    throw new Error("Unexpected status code: " + response.status);
  } catch (error: any) {
    const errorMessage = error.response?.data?.error_message || error.message || "Failed to link vending machine.";
    throw new Error(errorMessage);
  }
};

export const syncMachineLoginToShakeHQ = async ({
  machineId,
  machineSession,
}: {
  machineId: number | null;
  machineSession: string | null;
}) => {
  if (!machineId || !machineSession) {
    return false;
  }

  try {
    let couponsForPayload: any[] = [];
    let customerId: number | null = null;
    let userName: string | null = null;
    let userEmail: string | null = null;
    let userTelephone: string | null = null;
    let availablePoints: number | null = 0;

    try {
      const userCoupons = await GetUserCoupon(PATH_NAME.API_GET_USER_VOUCHERS);
      couponsForPayload = normalizeMachineCouponList(userCoupons);
    } catch (error) {
      console.error("Failed to fetch user coupons for machine login.", error);
      couponsForPayload = [];
    }

    try {
      const profileResponse = await UserProfile();
      const profileData = profileResponse?.result ?? profileResponse?.data?.result ?? profileResponse ?? null;

      customerId = profileData?.id ?? profileData?.customer_id ?? null;
      userName = [profileData?.firstname, profileData?.lastname].filter(Boolean).join(" ") || null;
      userEmail = profileData?.email ?? null;
      userTelephone = profileData?.full_mobile ?? null;
    } catch (error) {
      console.error("Failed to fetch profile for machine login.", error);
    }

    try {
      const rewardPointsResponse = await axiosInstance.get(PATH_NAME.API_USER_GET_REWARD_POINTS_BALANCE);
      availablePoints = normalizeMachineRewardPoints(rewardPointsResponse?.data);
    } catch (error) {
      console.error("Failed to fetch reward points for machine login.", error);
      availablePoints = 0;
    }

    await sendMachineLoginToShakeHQ({
      customer_id: customerId,
      customer_group_id: "group_a",
      name: userName,
      machine_id: machineId,
      machine_session: machineSession,
      email: userEmail,
      telephone: userTelephone,
      customer_token: "token-xyz",
      available_points: availablePoints,
      coupons: couponsForPayload,
      rewards: availablePoints,
      reward_earn_rule: {
        reward_earn_rule_id: 2,
        spend: 1,
        earn: 1,
      },
    });

    const params = new URLSearchParams(window.location.search);
    params.delete("q");

    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
    window.history.replaceState({}, "", nextUrl);

    return true;
  } catch (error: any) {
    const params = new URLSearchParams(window.location.search);
    params.delete("q");

    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
    window.history.replaceState({}, "", nextUrl);

    throw new Error(error.message || "Failed to link vending machine.");
  }
};
